import React, { useState, useCallback } from 'react';
import { API_URL } from '../../../utils/api';

/**
 * TestkundPanel — listar testkunder och låter admin rensa procurement-artefakter
 * som inte går att ta bort via vanliga UI:t (hembesök, estimat-uppföljning,
 * kalkyltid, projekteringstid). Avbokar även kopplade kalenderhändelser.
 */
const ARTIFACTS = [
  { key: 'homeVisit', label: 'Hembesök' },
  { key: 'followup', label: 'Estimat-uppföljning' },
  { key: 'calculationTime', label: 'Kalkyltid' },
  { key: 'projekteringTime', label: 'Projekteringstid' },
  { key: 'offert', label: 'Offert-mail & meddelande' },
  { key: 'komIgang', label: 'Kom igång (publicerat för kund)' },
  { key: 'estimatGodkant', label: 'Godkänt estimat' },
  { key: 'offertGodkand', label: 'Accepterad offert' },
];

const TestkundPanel = ({ testCustomers = [] }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [stateById, setStateById] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [clearing, setClearing] = useState(null); // `${customerId}:${part}`

  const fetchState = useCallback(async (customerId) => {
    setLoadingId(customerId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/procurement/test-customer/${customerId}/state`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStateById(prev => ({ ...prev, [customerId]: res.ok ? data.data : { error: data.message || 'Fel' } }));
    } catch (e) {
      setStateById(prev => ({ ...prev, [customerId]: { error: e.message } }));
    } finally {
      setLoadingId(null);
    }
  }, []);

  const toggle = (customerId) => {
    if (expandedId === customerId) { setExpandedId(null); return; }
    setExpandedId(customerId);
    if (!stateById[customerId]) fetchState(customerId);
  };

  // Godkännanden backas i stället för att rensas — annan formulering behövs
  const AR_BACKNING = ['estimatGodkant', 'offertGodkand'];

  const clearPart = async (customerId, part, label) => {
    const fraga = AR_BACKNING.includes(part)
      ? `Backa "${label}" för denna testkund? Godkännandet tas bort och statusen går tillbaka ett steg.`
      : `Rensa "${label}" för denna testkund? Ev. kalenderhändelse avbokas.`;
    if (!window.confirm(fraga)) return;
    setClearing(`${customerId}:${part}`);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/procurement/test-customer/${customerId}/clear`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ part })
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || `HTTP ${res.status}`); }
      await fetchState(customerId);
    } catch (e) {
      alert(`Kunde inte rensa: ${e.message}`);
    } finally {
      setClearing(null);
    }
  };

  const summarize = (key, st) => {
    if (key === 'homeVisit') {
      const h = st.homeVisit || {};
      const p = [];
      if (h.booked) p.push('bokat');
      if (h.proposals) p.push(`${h.proposals} förslag`);
      if (h.skipped) p.push('hoppat över');
      return p.length ? p.join(', ') : '—';
    }
    if (key === 'followup') {
      const f = st.followup || {};
      const p = [];
      if (f.booked) p.push('bokat');
      if (f.proposals) p.push(`${f.proposals} förslag`);
      return p.length ? p.join(', ') : '—';
    }
    if (key === 'estimatGodkant') {
      if (!st.estimatGodkant?.godkant) return st.estimatGodkant?.status || '—';
      return st.estimatGodkant.godkandAvKonsult ? 'godkänt av BK åt kund' : st.estimatGodkant.status;
    }
    if (key === 'offertGodkand') {
      if (st.offertGodkand?.harProjekt) return 'har blivit projekt — går inte att backa';
      if (!st.offertGodkand?.accepterad) return '—';
      return st.offertGodkand.godkandAvKonsult ? 'accepterad av BK åt kund' : 'accepterad';
    }
    if (key === 'calculationTime') return st.calculationTime?.booked ? 'bokat' : '—';
    if (key === 'projekteringTime') {
      if (!st.projekteringTime?.hasProject) return 'inget projekt';
      return st.projekteringTime?.booked ? 'bokat' : '—';
    }
    if (key === 'offert') {
      const o = st.offert || {};
      const p = [];
      if (o.mailSent) p.push('mail skickat');
      if (o.hasMessage) p.push('meddelande');
      return p.length ? p.join(', ') : '—';
    }
    if (key === 'komIgang') {
      const k = st.komIgang || {};
      if (!k.hasProject) return 'inget projekt';
      const p = [];
      if (k.shoppingList) p.push('produktval');
      if (k.rumsa) p.push('rumsa');
      if (k.timeline) p.push('tidplan');
      return p.length ? p.join(', ') : '—';
    }
    return '';
  };

  const hasSomething = (key, st) => {
    if (key === 'homeVisit') return !!(st.homeVisit?.booked || st.homeVisit?.proposals > 0 || st.homeVisit?.skipped);
    if (key === 'followup') return !!(st.followup?.booked || st.followup?.proposals > 0);
    if (key === 'calculationTime') return !!st.calculationTime?.booked;
    if (key === 'projekteringTime') return !!st.projekteringTime?.booked;
    if (key === 'offert') return !!(st.offert?.mailSent || st.offert?.hasMessage);
    if (key === 'komIgang') return !!(st.komIgang?.shoppingList || st.komIgang?.rumsa || st.komIgang?.timeline);
    if (key === 'estimatGodkant') return !!st.estimatGodkant?.godkant;
    // Har offerten blivit projekt måste projektet bort först — knappen låses
    if (key === 'offertGodkand') return !!st.offertGodkand?.accepterad && !st.offertGodkand?.harProjekt;
    return false;
  };

  if (testCustomers.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow p-6 text-gray-500">
        Inga testkunder. Markera en kund som "Testkund" i användar-redigeringen.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">
        Rensa procurement-data som inte går att ta bort via vanliga UI:t. Endast testkunder visas här.
      </p>
      {testCustomers.map(u => {
        const st = stateById[u._id];
        const expanded = expandedId === u._id;
        return (
          <div key={u._id} className="bg-white rounded-xl shadow border border-gray-200">
            <button
              onClick={() => toggle(u._id)}
              className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50"
            >
              <div>
                <span className="font-medium text-gray-900">{u.firstName} {u.lastName}</span>
                <span className="text-sm text-gray-500 ml-2">{u.email}</span>
                {st?.status && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-pink-50 text-leadme-pink">{st.status}</span>
                )}
              </div>
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {expanded && (
              <div className="px-5 pb-4 border-t border-gray-100">
                {loadingId === u._id && !st && <div className="py-3 text-sm text-gray-500">Laddar…</div>}
                {st?.error && <div className="py-3 text-sm text-red-600">{st.error}</div>}
                {st && !st.error && !st.hasProcurement && (
                  <div className="py-3 text-sm text-gray-500">Ingen upphandling för denna kund.</div>
                )}
                {st && !st.error && st.hasProcurement && (
                  <ul className="divide-y divide-gray-100 mt-1">
                    {ARTIFACTS.map(a => {
                      const present = hasSomething(a.key, st);
                      const isClearing = clearing === `${u._id}:${a.key}`;
                      return (
                        <li key={a.key} className="flex items-center justify-between py-2.5">
                          <div className="text-sm">
                            <span className="font-medium text-gray-800">{a.label}</span>
                            <span className="text-gray-500 ml-2">{summarize(a.key, st)}</span>
                          </div>
                          <button
                            onClick={() => clearPart(u._id, a.key, a.label)}
                            disabled={!present || isClearing}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {isClearing
                              ? (AR_BACKNING.includes(a.key) ? 'Backar…' : 'Rensar…')
                              : (AR_BACKNING.includes(a.key) ? 'Backa' : 'Rensa')}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TestkundPanel;
